import { gql } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';

const GOAL_OBJECT_FIELDS =
  'id name type __typename householdClientGroup { id name } statistics { marketValueCents } user { id firstName lastName entityName organization { name } }';
const SUBACCOUNT_OBJECT_FIELDS =
  'id __typename account { id type taxRank custodianAccountName user { id firstName lastName entityName organization { name } } } goal { id name type householdClientGroup { id name } } statistics { marketValueCents }';
const SUBACCOUNT_BASE_FIELDS =
  'subAccount { id state account { taxRank type custodianAccountName user { firstName entityName } } } currency cashCents expectedCashCents cashAvailableForTradeCents marketValueCents pendingWithdrawCents';

// GraphQL's native `@include(if: ...)` directive on a single static query —
// Apollo Client resolves the field only when the `canReadRepCode` variable is
// true, so the query stays statically typed/cacheable and codegen-friendly.
const SUBACCOUNT_OBJECT_FIELDS_WITH_REP_CODE =
  'id __typename account { id type taxRank custodianAccountName repCode @include(if: $canReadRepCode) { code name } user { id firstName lastName entityName organization { name } } } goal { id name type householdClientGroup { id name } } statistics { marketValueCents }';
const SUBACCOUNT_BASE_FIELDS_WITH_REP_CODE =
  'subAccount { id state account { taxRank type custodianAccountName repCode @include(if: $canReadRepCode) { code name } user { firstName entityName } } } currency cashCents expectedCashCents cashAvailableForTradeCents marketValueCents pendingWithdrawCents';
const HOLDING_FIELDS =
  'currency expectedValueCents expectedPercentage valueCents quantity pendingQuantity taxRank isCashEquivalent financialProduct { id ticker isCash currentPriceCents name taxRank currency translatedName { en fr } primaryAssetClass { key translatedName { en } } secondaryAssetClass { key translatedName { en } } }';
const MODEL_PORTFOLIO_FIELDS = `id translatedName { ${TRANSLATED_STRING_FIELDS} }`;
const PORTFOLIO_CORE_FIELDS =
  'cashCents pendingCashCents expectedCashCents cashAvailableForTradeCents expectedCashPercentage marketValueCents pendingSubTradeRequests unverifiedSubTradeRequests driftRebalanceRequired transferRebalanceRequired liquidateRebalanceRequired state updatedAt';
// NOTE: `pendingContributionCents`, `pendingCashCents`, and
// `cashOnHoldToTradeCents` are intentionally NOT selected. The backend declares
// these SubAccountReport fields as non-nullable but can return null for them,
// which makes the GraphQL layer reject the entire response ("Cannot return null
// for non-nullable field …"). They are unused by the hub, so omitting them
// avoids breaking the Portfolios / Blotter views.

export const FETCH_DASHBOARD_STATS = gql`
  query fetchDashboardStats($organizationId: ObjectID!) {
    portfolioReports: fetchPortfolioReports(
      input: {
        filter: { organizationId: $organizationId }
        pagination: { perPage: 1000, offSet: 0 }
      }
    ) {
      totalCount
      portfolioReports {
        id
        modelPortfolioType
        driftRebalanceRequired
        liquidateRebalanceRequired
        transferRebalanceRequired
        pendingSubTradeRequests
        unverifiedSubTradeRequests
        marketValueCents
        cashCents
        expectedCashCents
        object {
          ... on Goal {
            id
            name
            householdClientGroup {
              name
            }
            user {
              firstName
              lastName
            }
          }
          ... on SubAccount {
            id
            account {
              type
              user {
                firstName
                lastName
              }
            }
            goal {
              name
              householdClientGroup {
                name
              }
            }
          }
        }
      }
    }
    activeOptimizers: fetchPortfolioOptimizers(
      input: {
        filter: { organizationId: $organizationId }
        pagination: { perPage: 100, offSet: 0 }
      }
    ) {
      portfolioOptimizers {
        id
        state
        numPortfolios
        createdAt
      }
    }
    activeBulkRuns: fetchBulkTradeRuns(
      input: {
        filter: { organizationId: $organizationId }
        pagination: { perPage: 100, offSet: 0 }
      }
    ) {
      bulkTradeRuns {
        id
        state
        createdAt
        allTradeRequestsByExchange {
          total
        }
      }
    }
    initiatedTrades: fetchSubTradeRequests(
      input: {
        filter: { organizationId: $organizationId, states: [INITIATED] }
        pagination: { perPage: 1, offSet: 0 }
      }
    ) {
      totalCount
    }
    readyTrades: fetchSubTradeRequests(
      input: {
        filter: { organizationId: $organizationId, states: [READY] }
        pagination: { perPage: 1, offSet: 0 }
      }
    ) {
      totalCount
    }
    requestedTrades: fetchSubTradeRequests(
      input: {
        filter: { organizationId: $organizationId, states: [REQUESTED] }
        pagination: { perPage: 1, offSet: 0 }
      }
    ) {
      totalCount
    }
  }
`;

export const FETCH_MODEL_PORTFOLIOS = gql`
  query fetchModelPortfolios($input: FetchModelPortfoliosInput!) {
    fetchModelPortfolios(input: $input) {
      modelPortfolios {
        id
        name
        translatedName { ${TRANSLATED_STRING_FIELDS} }
        state
      }
    }
  }
`;

export const FETCH_PORTFOLIO_REPORTS = gql`
  query fetchPortfolioReports($input: FetchPortfolioReportsInput!, $canReadRepCode: Boolean = false) {
    fetchPortfolioReports(input: $input) {
      portfolioReports {
        id modelPortfolioType baseCurrency originalCurrencies refreshState
        object { ... on Goal { ${GOAL_OBJECT_FIELDS} } ... on SubAccount { ${SUBACCOUNT_OBJECT_FIELDS_WITH_REP_CODE} } }
        modelPortfolio { ${MODEL_PORTFOLIO_FIELDS} }
        ${PORTFOLIO_CORE_FIELDS}
        pendingSubTradeRequestAllocations
        lastOptimizedBy { id createdAt }
        bulkTradeRunReasons { reason total unblockedSince }
        holdings { ${HOLDING_FIELDS} }
        subAccounts { ${SUBACCOUNT_BASE_FIELDS_WITH_REP_CODE} }
      }
      totalCount
    }
  }
`;

export const FETCH_PORTFOLIO_OPTIMIZERS = gql`
  query fetchPortfolioOptimizers($input: FetchPortfolioOptimizersInput!) {
    fetchPortfolioOptimizers(input: $input) {
      portfolioOptimizers {
        id
        type
        state
        organization {
          id
          name
        }
        numPortfolios
        expectedTotalSubTradeRequests
        subTradeRequestsCreated
        createdAt
      }
      totalCount
    }
  }
`;

export const FETCH_BULK_TRADE_RUNS = gql`
  query fetchBulkTradeRuns($input: FetchBulkTradeRunsInput!) {
    fetchBulkTradeRuns(input: $input) {
      bulkTradeRuns {
        id
        state
        createdAt
        updatedAt
        organization {
          id
          name
        }
        incompleteTradeRequestsByExchange {
          exchange
          total
        }
        allTradeRequestsByExchange {
          exchange
          total
        }
      }
      totalCount
    }
  }
`;

export const FETCH_BULK_TRADE_RUN_DETAIL = gql`
  query fetchBulkTradeRunDetail($bulkTradeRunId: ObjectID!) {
    fetchBulkTradeRun(bulkTradeRunId: $bulkTradeRunId) {
      bulkTradeRun {
        id
        state
        createdAt
        updatedAt
        buyBulkTradeFileUrl
        sellBulkTradeFileUrl
        mutualFundFileUrl
        incompleteTradeRequestsByExchange {
          exchange
          total
        }
        allTradeRequestsByExchange {
          exchange
          total
        }
        bulkTradeRequests {
          type
          bulkTrades {
            _id
            exchange
            financialProduct {
              id
              ticker
              name
              exchange
            }
            total
            actualTotal
            expectedTradePriceCents
            actualTradePriceCents
            expectedTradePriceInCurrencyCents
            actualTradePriceInCurrencyCents
            currency
          }
        }
        bulkMutualFundTrades {
          id
          type
        }
      }
    }
  }
`;

export const FETCH_BULK_TRADE_RUN_ALLOCATIONS = gql`
  query fetchBulkTradeRunAllocations(
    $input: FetchBulkTradeRunAllocationsInput!
  ) {
    fetchBulkTradeRunAllocations(input: $input) {
      bulkTradeRunAllocations {
        id
        externalId
        account
        settlementCurrency
        tradeCurrency
        side
        symbol
        market
        fillPrice
        shareQuantity
        tradeDate
        settlementDate
        tradeValue
        fXRate
      }
      totalCount
    }
  }
`;

export const FETCH_TRADE_REQUESTS_FOR_BATCH = gql`
  query fetchTradeRequests($input: FetchTradeRequestsInput!) {
    fetchTradeRequests(input: $input) {
      tradeRequests {
        id
        actualQuantity
        actualTradePriceCents
        expectedQuantity
        expectedTradePriceCents
        moneyAllocatedCents
        state
        type
        currency
        settlementDate
        createdAt
        hasPairs
        account {
          type
          user {
            id
            firstName
            lastName
            entityName
          }
        }
        financialProduct {
          ticker
          exchange
          translatedName {
            en
          }
        }
      }
      totalCount
    }
  }
`;

export const FETCH_BULK_TRADE_REQUESTS_BY_SOURCE = gql`
  query fetchBulkTradeRequests($input: FetchBulkTradeRequestsInput!) {
    fetchBulkTradeRequests(input: $input) {
      bulkTradeRequests {
        id
        state
      }
      totalCount
    }
  }
`;

export const FETCH_SUB_TRADE_REQUEST_STATS = gql`
  query fetchSubTradeRequests($input: FetchSubTradeRequestsInput!) {
    fetchSubTradeRequests(input: $input) {
      totalCount
    }
  }
`;

export const FETCH_SUB_TRADE_REQUESTS_BLOCKED_CHECK = gql`
  query fetchSubTradeRequestsBlockedCheck($input: FetchSubTradeRequestsInput!) {
    fetchSubTradeRequests(input: $input) {
      subTradeRequests {
        id
        state
        type
        isSellAll
        moneyAllocatedCents
        expectedQuantity
        expectedTradePriceCents
        actualQuantity
        actualTradePriceCents
        currency
        settlementDate
        createdAt
        financialProduct {
          id
          ticker
          currentPriceCents
          settlementDays
          currency
          exchange
          translatedName {
            en
          }
        }
        subAccount {
          id
          goal {
            id
          }
          account {
            id
            type
            user {
              id
              firstName
              lastName
            }
          }
        }
        portfolioReport
        preSplitMoneyAllocatedCents
        isProcessedForPair
        pair {
          id
          financialProduct {
            ticker
          }
        }
        bulkTradeRun {
          id
        }
        verifiedAt
        verifiedByOrganizationUser {
          firstName
          lastName
        }
        blockedUntil
        blockedUntilAccountActive
        blockedUntilSubAccountActive
        schedule {
          id
          name
          nextDate
        }
      }
      totalCount
    }
  }
`;

export const FETCH_SUB_TRADE_REQUESTS_FULL = gql`
  query fetchSubTradeRequestsFull($input: FetchSubTradeRequestsInput!) {
    fetchSubTradeRequests(input: $input) {
      subTradeRequests {
        id
        state
        type
        isSellAll
        moneyAllocatedCents
        expectedQuantity
        expectedTradePriceCents
        actualQuantity
        actualTradePriceCents
        currency
        settlementDate
        createdAt
        financialProduct {
          id
          ticker
          currentPriceCents
          settlementDays
          currency
          exchange
          translatedName {
            en
          }
        }
        subAccount {
          id
          goal {
            id
          }
          account {
            id
            type
            user {
              id
              firstName
              lastName
            }
          }
        }
        portfolioReport
        preSplitMoneyAllocatedCents
        isProcessedForPair
        pair {
          id
          financialProduct {
            ticker
          }
        }
        bulkTradeRun {
          id
        }
        verifiedAt
        verifiedByOrganizationUser {
          firstName
          lastName
        }
        blockedUntil
        blockedUntilAccountActive
        blockedUntilSubAccountActive
        schedule {
          id
          name
          nextDate
        }
      }
      totalCount
    }
  }
`;

export const FETCH_RECENT_TRADE_TRANSACTIONS = gql`
  query fetchRecentTradeTransactions($input: FetchTransactionsInput!) {
    fetchTransactions(input: $input) {
      transactions {
        id
        type
        quantity
        priceCents
        valueCents
        currency
        date
        settleDate
        pending
        description
        objectType
        objectId
        subAccount {
          id
          account {
            id
            type
            user {
              id
              firstName
              lastName
            }
          }
        }
        financialProduct {
          id
          ticker
          translatedName {
            en
          }
        }
      }
      totalCount
    }
  }
`;

export const FETCH_UPCOMING_TRANSFERS = gql`
  query fetchUpcomingTransfers($input: FetchUpcomingTransfersInput!) {
    fetchUpcomingTransfers(input: $input) {
      upcomingTransfers {
        id
        amountCents
        type
        source
        state
        frequency
        scheduledDate
        createdAt
        currency
        isRecurring
        withdrawalReason
        subAccount {
          id
          account {
            id
            type
            user {
              id
              firstName
              lastName
            }
          }
          goal {
            id
            name
          }
          statistics {
            marketValueCents
            currency
          }
        }
        fromSubAccount {
          id
          account {
            id
            type
            user {
              id
              firstName
              lastName
            }
          }
          goal {
            id
            name
          }
        }
      }
      totalCount
    }
  }
`;

export const FETCH_BLOTTER_DETAIL = gql`
  query fetchBlotterDetail($input: FetchPortfolioReportsInput!) {
    fetchPortfolioReports(input: $input) {
      portfolioReports {
        id modelPortfolioType baseCurrency originalCurrencies
        object { ... on Goal { ${GOAL_OBJECT_FIELDS} } ... on SubAccount { ${SUBACCOUNT_OBJECT_FIELDS} } }
        modelPortfolio { ${MODEL_PORTFOLIO_FIELDS} }
        fxRates { from to rate }
        ${PORTFOLIO_CORE_FIELDS}
        pendingSubTradeRequestAllocations
        holdings { ${HOLDING_FIELDS} }
        subAccounts { ${SUBACCOUNT_BASE_FIELDS} accountBaseCurrency holdings { ${HOLDING_FIELDS} } }
      }
      totalCount
    }
  }
`;

export const FETCH_BATCH_PRICE_CONTEXT = gql`
  query FetchBatchPriceContext($input: FinancialProductPriceHistoryInput!) {
    fetchFinancialProductPriceHistory(input: $input) {
      priceHistory {
        financialProductId
        date
        priceCents
      }
    }
  }
`;

export const FETCH_OPTIMIZED_PORTFOLIO_REPORTS = gql`
  query fetchOptimizedPortfolioReports(
    $input: FetchOptimizedPortfolioReportsInput!
  ) {
    fetchOptimizedPortfolioReports(input: $input) {
      portfolioReports {
        id
        driftRebalanceRequired
        liquidateRebalanceRequired
        transferRebalanceRequired
        pendingSubTradeRequests
        expectedCashCents
        cashCents
        marketValueCents
        modelPortfolio { ${MODEL_PORTFOLIO_FIELDS} }
        object {
          ... on Goal {
            id
            name
            user {
              firstName
              lastName
            }
          }
          ... on SubAccount {
            id
            account {
              type
              user {
                firstName
                lastName
              }
            }
            goal {
              name
            }
          }
        }
        holdings {
          expectedPercentage
          expectedValueCents
          valueCents
          quantity
          driftRebalanceRequired
          financialProduct {
            ticker
            translatedName {
              en
            }
            isCash
          }
        }
      }
      totalCount
    }
  }
`;

export const FETCH_TRADE_ROW_POSITION = gql`
  query fetchTradeRowPosition($input: FetchPortfolioReportsInput!) {
    fetchPortfolioReports(input: $input) {
      portfolioReports {
        id
        modelPortfolioType
        marketValueCents
        cashAvailableForTradeCents
        holdings {
          quantity
          valueCents
          expectedPercentage
          financialProduct {
            id
            ticker
            currentPriceCents
          }
        }
        subAccounts {
          subAccount {
            id
          }
          cashAvailableForTradeCents
          marketValueCents
        }
      }
    }
  }
`;

export const FETCH_TRADE_ROW_PIPELINE = gql`
  query fetchTradeRowPipeline($input: FetchSubTradeRequestsInput!) {
    fetchSubTradeRequests(input: $input) {
      subTradeRequests {
        id
        state
        type
        moneyAllocatedCents
        createdAt
      }
      totalCount
    }
  }
`;

export const FETCH_TRADE_ROW_SETTLED = gql`
  query fetchTradeRowSettled($input: FetchTransactionsInput!) {
    fetchTransactions(input: $input) {
      transactions {
        id
        type
        quantity
        priceCents
        valueCents
        currency
        date
      }
      totalCount
    }
  }
`;

export const FETCH_PORTFOLIO_OPTIMIZER_STATUS = gql`
  query fetchPortfolioOptimizer($input: FetchPortfolioOptimizerInput!) {
    fetchPortfolioOptimizer(input: $input) {
      portfolioOptimizer {
        state
        numPortfolios
        expectedTotalSubTradeRequests
        subTradeRequestsCreated
      }
    }
  }
`;

export const FETCH_OPTIMAL_PORTFOLIO = gql`
  query fetchOptimalPortfolio($input: FetchOptimalPortfolioInput!) {
    fetchOptimalPortfolio(input: $input) {
      optimalPortfolio {
        id
        modelPortfolio
        modelPortfolioType
        expectedCashPercentage
        expectedCashCents
        subAccounts {
          index
          subAccount
          account
          user
          goal
          marketValueCents
          expectedCashCents
          cashCentsDeviation
          cashCentsToInvest
          taxable
          taxRank
          optimalHoldings {
            index
            financialProduct
            expectedValueCents
            valueCents
            taxRank
          }
          currentHoldingAllocations
          currentHoldingQuantities
          preTradeHoldingAllocations
          preTradeHoldingPurchaseAllocations
        }
        optimalHoldings {
          index
          financialProduct
          expectedValueCents
          valueCents
          taxRank
        }
        preTradeHoldings {
          index
          financialProduct
          moneyAllocatedCents
          diffValueCents
          diffPercentage
          expectedPercentage
          taxRank
          moneyAllocatedCentsRedemptions
          moneyAllocatedCentsPurchases
          diffPercentagePurchasesStage1
        }
        lastOptimizedBy
      }
    }
  }
`;
